import React, { useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    CircularProgress,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Grid
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { FinanceService } from '../../../api/services/financeService';
import { toast } from '../../../utils/toast';

export default function ExportLedgerModal({ open, onClose }) {
    const [isExporting, setIsExporting] = useState(false);
    
    // Default dates
    const today = new Date().toISOString().split('T')[0];
    const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0];

    const [form, setForm] = useState({
        startDate: thirtyDaysAgo,
        endDate: today,
        format: 'csv'
    });

    const handleChange = (field) => (event) => {
        setForm(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleExport = async () => {
        if (!form.startDate || !form.endDate) {
            toast.error('Start and End dates are required for export.');
            return;
        }

        if (new Date(form.startDate) > new Date(form.endDate)) {
            toast.error('Start date cannot be after end date.');
            return;
        }

        setIsExporting(true);
        try {
            const params = {
                startDate: form.startDate,
                endDate: form.endDate,
                format: form.format
            };

            const response = await FinanceService.exportLedger(params);
            
            // The API returns a JSON response: { status: 'success', message: 'Export started...' }
            const successMessage = response.message || 'Export started. You will receive an email shortly.';
            toast.success(successMessage);
            onClose();
        } catch (error) {
            console.error('Failed to export ledger:', error);
            toast.error('Failed to compile ledger export. Please check parameters or try again later.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Dialog open={open} onClose={!isExporting ? onClose : undefined} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: '1px solid #eee', bgcolor: '#fbfbfb', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                <FileDownloadOutlinedIcon sx={{ mr: 1 }} /> Export Ledger Logs
            </DialogTitle>
            <DialogContent sx={{ p: 4 }}>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Download financial reporting summaries into external file structures. Define the transaction window and preferred format below.
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Start Date"
                                type="date"
                                value={form.startDate}
                                onChange={handleChange('startDate')}
                                disabled={isExporting}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: form.endDate }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="End Date"
                                type="date"
                                value={form.endDate}
                                onChange={handleChange('endDate')}
                                disabled={isExporting}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: form.startDate }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Export Format</InputLabel>
                                <Select
                                    value={form.format}
                                    label="Export Format"
                                    onChange={handleChange('format')}
                                    disabled={isExporting}
                                >
                                    <MenuItem value="csv">CSV (Comma Separated Values)</MenuItem>
                                    <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                                    <MenuItem value="pdf">PDF Document</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: '1px solid #eee', bgcolor: '#fbfbfb' }}>
                <Button onClick={onClose} color="inherit" disabled={isExporting}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleExport} 
                    variant="contained" 
                    color="primary"
                    disabled={isExporting}
                    startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                    sx={{ px: 3 }}
                >
                    {isExporting ? 'Compiling File...' : 'Download Reporting File'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
